import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { CheckCircle, Settings, Workflow, Database, Zap, TrendingUp } from 'lucide-react';
import { ContactForm } from '../components/ContactForm';
import { SEO } from '../components/SEO';

export const Home = () => {
  const services = [
    {
      icon: <Settings className="w-12 h-12 text-[#00890b]" />,
      title: 'Windchill PDMLink',
      description: 'Comprehensive Change Management and Configuration Management solutions',
      link: '/hire-pdmlink-customization-expert',
    },
    {
      icon: <Workflow className="w-12 h-12 text-[#00890b]" />,
      title: 'Windchill ProjectLink',
      description: 'Project management and collaboration tools for engineering teams',
      link: '/hire-projectlink-customization-expert',
    },
    {
      icon: <Database className="w-12 h-12 text-[#00890b]" />,
      title: 'Supplier Management',
      description: 'Streamline supplier collaboration and supply chain management',
      link: '/hire-supplier-management-expert',
    },
    {
      icon: <Zap className="w-12 h-12 text-[#00890b]" />,
      title: 'Creo View & Visualization',
      description: 'Advanced visualization and viewing capabilities for CAD data',
      link: '/hire-creo-view-visualization-expert',
    },
    {
      icon: <Settings className="w-12 h-12 text-[#00890b]" />,
      title: 'Infrastructure',
      description: 'Types, Attributes, OIRs, Life Cycles, Workflows, and Configuration',
      link: '/hire-infrastructure-customization-expert',
    },
    {
      icon: <TrendingUp className="w-12 h-12 text-[#00890b]" />,
      title: 'Customization',
      description: 'APIs, Form Processors, Table Builders, Data Utilities, and Wizards',
      link: '/hire-windchill-customization-expert',
    },
  ];

  const customizations = [
    'Custom Actions',
    'Filters',
    'Data Utility',
    'Validator',
    'Form Processors',
    'Wizard',
    'Table Builder',
    'Events/Listeners',
    'Life Cycles',
    'OIRs',
    'Workflows',
  ];

  const caseStudies = [
    {
      title: 'Enterprise PLM Transformation',
      company: 'Fortune 500 Manufacturing Company',
      challenge: 'Legacy Windchill system hindering product development efficiency',
      solution: 'Comprehensive Windchill upgrade and customization with advanced workflow automation',
      result: '40% reduction in change order processing time and 30% improvement in collaboration',
    },
    {
      title: 'Global Supplier Integration',
      company: 'Aerospace & Defense Contractor',
      challenge: 'Complex supplier network requiring real-time data synchronization',
      solution: 'Custom Windchill SUMA implementation with automated supplier portals',
      result: '50% faster supplier onboarding and 60% reduction in data discrepancies',
    },
    {
      title: 'Configuration Management Excellence',
      company: 'Automotive OEM',
      challenge: 'Managing thousands of product variants across global facilities',
      solution: 'Advanced Windchill Configuration Management with custom validation rules',
      result: '35% reduction in configuration errors and improved traceability',
    },
  ];

  return (
    <div className="min-h-screen">
      <SEO 
        title="Hire Windchill Customization Experts | Outsource Windchill Development"
        description="Hire expert Windchill developers and outsource your customization needs. 15+ years experience in PDMLink, ProjectLink, SUMA, upgrades. Dedicated Windchill customization experts for hire."
        keywords="hire windchill developers, outsource windchill customization, hire windchill experts, windchill customization services, outsource windchill development, hire pdmlink expert, hire windchill consultant"
        canonical="/"
      />
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-white via-green-50 to-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <Badge className="mb-6 bg-[#00890b] hover:bg-[#006a08] text-white px-4 py-2 text-sm font-semibold">
              15+ Years of Windchill Expertise
            </Badge>
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
              Expert Windchill
              <span className="text-[#00890b]"> Customization</span>
              <br />& Development Services
            </h1>
            <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
              Transform your PLM experience with professional Windchill customization,
              development, and migration services from certified experts
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => {
                  const contactForm = document.getElementById('contact-form');
                  if (contactForm) {
                    contactForm.scrollIntoView({ behavior: 'smooth' });
                  }
                }}
                className="bg-[#00890b] hover:bg-[#006a08] text-white px-8 py-6 text-lg"
              >
                Get Started
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  const servicesSection = document.getElementById('services');
                  if (servicesSection) {
                    servicesSection.scrollIntoView({ behavior: 'smooth' });
                  }
                }}
                className="border-[#00890b] text-[#00890b] hover:bg-green-50 px-8 py-6 text-lg"
              >
                Explore Services
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-4xl font-bold text-[#00890b] mb-2">15+</div>
              <div className="text-gray-600">Years Experience</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-[#00890b] mb-2">200+</div>
              <div className="text-gray-600">Projects Delivered</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-[#00890b] mb-2">100%</div>
              <div className="text-gray-600">Client Satisfaction</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-[#00890b] mb-2">24/7</div>
              <div className="text-gray-600">Support Available</div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Windchill Services
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Comprehensive Windchill solutions tailored to your business needs
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <Card
                key={index}
                className="hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border-t-4 border-t-[#00890b]"
              >
                <CardHeader>
                  <div className="mb-4">{service.icon}</div>
                  <CardTitle className="text-xl">{service.title}</CardTitle>
                  <CardDescription>{service.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <Link to={service.link}>
                    <Button
                      variant="outline"
                      className="w-full border-[#00890b] text-[#00890b] hover:bg-green-50"
                    >
                      Learn More
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Customizations Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Windchill Customization Capabilities
            </h2>
            <p className="text-lg text-gray-600">
              Expert development across all Windchill customization areas
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {customizations.map((item, index) => (
              <div
                key={index}
                className="flex items-center space-x-2 p-4 bg-gray-50 rounded-lg hover:bg-green-50 transition-colors"
              >
                <CheckCircle className="w-5 h-5 text-[#00890b] flex-shrink-0" />
                <span className="text-gray-700 font-medium">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Case Studies Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Success Stories
            </h2>
            <p className="text-lg text-gray-600 mb-2">
              Real results from real Windchill implementations
            </p>
            <p className="text-sm text-gray-500 italic">
              Get your customized case studies from real Windchill developers
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {caseStudies.map((study, index) => (
              <Card key={index} className="hover:shadow-xl transition-shadow">
                <CardHeader>
                  <CardTitle className="text-xl">{study.title}</CardTitle>
                  <CardDescription className="font-semibold text-[#00890b]">
                    {study.company}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Challenge:</h4>
                    <p className="text-sm text-gray-600">{study.challenge}</p>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Solution:</h4>
                    <p className="text-sm text-gray-600">{study.solution}</p>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Result:</h4>
                    <p className="text-sm text-[#00890b] font-medium">{study.result}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <ContactForm />
    </div>
  );
};